import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Fragment, useEffect, useRef, useState } from "react";

import { StyledAnchor } from "@/components/common/styles";
import {
  marketingHref,
  newTabProps,
} from "@/components/marketing/marketingLinks";
import { HEADING_2 } from "@/components/marketing/marketingType";
import { BAR_Z_INDEX } from "@/components/marketing/header/tokens";
import {
  ACCENT,
  ACCENT_DEEP,
  BUTTON_GLOW_HOVER,
  FRAME,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import type { ComparisonBullet, ComparisonCell } from "@/content/landing/types";
import { LandingImage, LandingSection } from "../LandingPrimitives";
import { Lines } from "@/components/marketing/Lines";

/**
 * `/product-comparison`, the one page the shared blocks could not hold.
 *
 * Everything else on the site compares things in rows of short values, which
 * `MatrixBlock` draws. This page is a stack of titled sections, each a row of
 * three product columns whose cells are variously a bulleted paragraph, a
 * price under a "From", a volume-price ladder or a plain line, and it opens on
 * a bordered row of product cards and carries a sticky product bar past it.
 *
 * The published page states the same table twice, in two DOMs: a three-column
 * one above 992 and, below it, a two-column one whose columns are chosen from
 * dropdowns. Both are here, each hidden at the other's widths, because that is
 * how the published page does it and the two share no layout at all.
 */

type Comparison = Laid<"comparison">;
type Offer = Comparison["offers"][number];
type Row = Comparison["rows"][number];

/**
 * One `comp-content_row`: the heading it opens a section with where it opens
 * one, and the strips-and-answers it holds. A band holds more than one part
 * because the published `Features` box does: two tinted strips, each with its
 * own row of answers, under a single heading.
 */
type BandPart = { row: Row; strip: boolean };
type Band = {
  title?: string;
  titleIcon?: string;
  parts: Array<BandPart>;
};

/**
 * The published table draws one box per band and states its section grouping
 * three ways:
 *
 * - a row that names a `group` goes on a strip; the first row of that group
 *   heads the box with the group's name, and later rows of the same group join
 *   that box rather than opening one, which is how `Features` prints;
 * - a row with no group that carries a strip's own furniture, meaning a note, a
 *   mark or a flag row, opens a box of its own and still goes on a strip.
 *   `Capacity`, `Pool size` and `Bandwidth` reach us that way;
 * - anything else heads its own box, titled by the row, with no strip.
 */
/** Whether a row carries a strip's own furniture and so cannot head a box. */
function furnishesStrip(row: Row): boolean {
  return (
    row.note !== undefined || row.icon !== undefined || row.flags !== undefined
  );
}

function placeRows(rows: ReadonlyArray<Row>): ReadonlyArray<Band> {
  const bands: Band[] = [];
  let group: string | undefined;
  for (const row of rows) {
    const last = bands[bands.length - 1];
    if (row.group !== undefined) {
      if (row.group === group && last) {
        last.parts.push({ row, strip: true });
      } else {
        bands.push({ title: row.group, parts: [{ row, strip: true }] });
      }
      group = row.group;
      continue;
    }
    group = undefined;
    bands.push(
      last?.parts.at(-1)?.strip === true && furnishesStrip(row)
        ? { parts: [{ row, strip: true }] }
        : {
            title: row.title,
            titleIcon: row.titleIcon,
            parts: [{ row, strip: false }],
          }
    );
  }
  return bands;
}

/** Bands are keyed by the row they open on, which is unique across the table. */
function bandKey(band: Band): string {
  return band.parts[0]?.row.title ?? "";
}

/**
 * The published table sets its opening two boxes apart, closing them at 80
 * where the rest close at 32, and draws the first one's cells as bulleted
 * prose where the rest are lines.
 */
const LEAD_BOXES = 2;

/** `_12-px-txt.opacity-50`: the word over every price. Not a published field. */
const FROM_LABEL = "From";

/** `.sub-nav-compare_wrap`: how far the bar travels when it is not wanted. */
const STICKY_BAR_HEIGHT = 58;

/* ── the type the table is set in, measured on the published page ────────── */

/** `.header-compare_title` and `.sub-nav-title`. */
const PRODUCT_TITLE = {
  color: marketingPalette.ink,
  fontSize: 18.08,
  lineHeight: "21.696px",
  WebkitTextStrokeWidth: "0.3px",
} as const;

/** `._12-px-txt.opacity-50`, `.compare-from_price-txt`, `.compare-from_per-txt`. */
const FROM_TEXT = {
  color: marketingPalette.ink,
  fontSize: 12,
  lineHeight: "18px",
  opacity: 0.5,
} as const;
const FROM_PRICE = {
  color: marketingPalette.ink,
  fontSize: 16.96,
  fontWeight: 500,
  lineHeight: "20.352px",
} as const;
const FROM_UNIT = {
  color: marketingPalette.ink,
  fontSize: 10.08,
  lineHeight: "17.4384px",
  opacity: 0.6,
} as const;

/** `.header-compare_btn`, at its two published sizes. */
const BUY_BUTTON = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  backgroundColor: ACCENT,
  color: "common.white",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  whiteSpace: "nowrap",
  padding: "8px 20px 8px 17.6px",
  "&:hover": {
    backgroundColor: marketingPalette.accentHover,
    boxShadow: BUTTON_GLOW_HOVER,
  },
} satisfies SxProps<Theme>;
const BUY_BUTTON_WIDE = { padding: "12px 32px" } as const;

/** `.link-hover.is-small`. */
const PRICING_LINK = {
  color: marketingPalette.ink,
  fontSize: 14.08,
  lineHeight: "21.12px",
  "&:hover": { color: ACCENT_DEEP, textDecoration: "underline" },
} satisfies SxProps<Theme>;

/** `.comp-content_title`, and the rule under it. */
const SECTION_TITLE = {
  color: marketingPalette.ink,
  fontSize: 22,
  fontWeight: 400,
  lineHeight: "26.4px",
  WebkitTextStrokeWidth: "0.3px",
} as const;
const RULE = {
  width: 1,
  height: "1px",
  flexShrink: 0,
  backgroundColor: marketingPalette.outline,
} as const;

/** `.comp-content_grey-box` and what sits on it. */
const STRIP = {
  display: "flex",
  alignItems: "center",
  gap: "6.08px",
  p: "12px",
  borderRadius: "8px",
  backgroundColor: marketingPalette.surface,
} as const;
const STRIP_HEADING = {
  color: marketingPalette.ink,
  fontSize: 16,
  lineHeight: "19.2px",
  flex: "0 0 auto",
  WebkitTextStrokeWidth: "0.4px",
} as const;
/** `.fair-subject`: the caveat the strip sets against its right edge. */
const STRIP_NOTE = {
  width: 1,
  textAlign: "right",
  color: marketingPalette.ink,
  fontSize: 12,
  fontStyle: "italic",
  lineHeight: "18px",
  opacity: 0.6,
} as const;

/** `.comp-content_txt`, `.is-14px`, `.is-list`, and `.comp-content_heading`. */
const CELL_LINE = {
  width: 1,
  color: marketingPalette.ink,
  fontSize: 15.04,
  lineHeight: "22.56px",
  [MID]: { fontSize: 14.08, lineHeight: "21.12px" },
} satisfies SxProps<Theme>;
const CELL_PROSE = {
  width: 1,
  color: marketingPalette.ink,
  fontSize: 12.96,
  lineHeight: "19.44px",
} as const;
const CELL_HEADING = {
  width: 1,
  color: marketingPalette.ink,
  fontSize: 13.6,
  lineHeight: "16.32px",
  WebkitTextStrokeWidth: "0.4px",
} as const;

/** `._13-px-txt`, `.compare-capacity_price`, `.compare-capacity_txt`. */
const TIER_LABEL = {
  color: marketingPalette.ink,
  fontSize: 14.72,
  lineHeight: "22.08px",
} as const;
const TIER_PRICE = {
  color: marketingPalette.ink,
  fontSize: 16,
  lineHeight: "19.2px",
  WebkitTextStrokeWidth: "0.3px",
} as const;
const TIER_UNIT = {
  color: marketingPalette.ink,
  fontSize: 12,
  lineHeight: "16.8px",
  opacity: 0.6,
} as const;

/** `.comp-content_item`, and `.is-8px-gap` beside it. */
const CELL_COLUMN = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: 1,
  [TINY]: { gap: "11.2px" },
} satisfies SxProps<Theme>;
const CELL_STACK = {
  display: "flex",
  flexDirection: "column",
  gap: "4.8px",
  alignItems: "flex-start",
  justifyContent: "center",
  width: 1,
} as const;

/* ── the pieces both DOMs share ──────────────────────────────────────────── */

/** `.compare-from_wrap`: "From" over the price and the unit beside it. */
function PriceFrom({
  price,
  per,
  align,
}: {
  price: string;
  per?: string;
  align: "start" | "centre";
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        alignItems: align === "centre" ? "center" : "flex-start",
        justifyContent: "flex-start",
      }}
    >
      <Typography sx={FROM_TEXT}>{FROM_LABEL}</Typography>
      <Box sx={{ display: "flex", gap: "4.8px", alignItems: "flex-end" }}>
        <Typography sx={FROM_PRICE}>{price}</Typography>
        {per ? <Typography sx={FROM_UNIT}>{per}</Typography> : null}
      </Box>
    </Box>
  );
}

/**
 * `.header-compare_btn` and the link under it. An anchor rather than
 * `LandingButton`: this control is 36px tall with no arrow, and every
 * destination carries an express-checkout query string a typed `search` object
 * would re-serialise.
 */
function OfferActions({ offer, wide }: { offer: Offer; wide?: boolean }) {
  return (
    <>
      <StyledAnchor
        href={marketingHref(offer.buy.href ?? "")}
        {...newTabProps(offer.buy)}
        sx={[BUY_BUTTON, wide ? BUY_BUTTON_WIDE : {}]}
      >
        {offer.buy.label}
      </StyledAnchor>
      <StyledAnchor
        href={marketingHref(offer.pricing.href ?? "")}
        {...newTabProps(offer.pricing)}
        sx={PRICING_LINK}
      >
        {offer.pricing.label}
      </StyledAnchor>
    </>
  );
}

/* ── the table above 992 ─────────────────────────────────────────────────── */

/** `.header-compare_item.with-line`: one cell of the ruled product row. */
const CARD_CELL = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  width: 1,
  p: "32px",
} as const;
const CARD_CELL_RULED = {
  ...CARD_CELL,
  borderRight: `1px solid ${marketingPalette.wash}`,
} as const;

/** `.header-compare_list`: the icons-and-names row over the prices-and-buy one. */
function ProductCards({ block }: { block: Comparison }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: 1 }}>
      <Box
        sx={{
          display: "flex",
          border: `1px solid ${marketingPalette.wash}`,
          borderRadius: "8px",
        }}
      >
        {block.columns.map((column) => (
          <Box key={column.title} sx={CARD_CELL_RULED}>
            {column.icon ? (
              <LandingImage
                src={column.icon}
                eager
                sx={{ width: "72px", height: "72px" }}
              />
            ) : null}
            <Typography sx={PRODUCT_TITLE}>{column.title}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex" }}>
        {block.offers.map((offer, index) => (
          <Box key={index} sx={CARD_CELL}>
            <PriceFrom price={offer.from} per={offer.per} align="centre" />
            <OfferActions offer={offer} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * `.sub-nav-compare_wrap`: the product names, at 28px, while the table is what
 * fills the screen. The published bar slides in when the table's top passes the
 * viewport top and out again when the closing panel comes into view; it is
 * fixed under the header rather than sticky, so it clears the table's own rows.
 */
function StickyProductBar({
  block,
  table,
}: {
  block: Comparison;
  table: React.RefObject<HTMLDivElement | null>;
}) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const box = table.current?.getBoundingClientRect();
      setShown(
        box !== undefined && box.top <= 0 && box.bottom > window.innerHeight
      );
    };
    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [table]);
  return (
    <Box
      aria-hidden={!shown}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        mt: "70px",
        // Under the site bar, over the page. The published bar names no
        // z-index because nothing on that page competes with it; ours sits in
        // a section that is `position: relative`, so without a layer of its own
        // the price units and the fair-usage notes paint through it as they
        // scroll past.
        zIndex: BAR_Z_INDEX - 1,
        backgroundColor: "common.white",
        borderBottom: `1px solid ${marketingPalette.wash}`,
        boxShadow: "rgba(174, 174, 216, 0.2) 0px 7px 19px -11px",
        transform: shown ? "none" : `translateY(-${STICKY_BAR_HEIGHT}px)`,
        transition: "transform 0.4s ease",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        [NOT_WIDE]: { display: "none" },
      }}
    >
      <Box sx={{ maxWidth: `${FRAME.header}px`, mx: "auto", px: "16px" }}>
        <Box
          sx={{
            display: "flex",
            borderTop: `1px solid ${marketingPalette.wash}`,
            borderLeft: `1px solid ${marketingPalette.wash}`,
            borderRight: `1px solid ${marketingPalette.wash}`,
          }}
        >
          {block.columns.map((column) => (
            <Box
              key={column.title}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6.4px",
                width: 1,
                py: "14.08px",
                px: "12px",
                borderRight: `1px solid ${marketingPalette.wash}`,
              }}
            >
              {column.icon ? (
                <LandingImage
                  src={column.icon}
                  sx={{ width: "28px", height: "28px", flexShrink: 0 }}
                />
              ) : null}
              <Typography sx={PRODUCT_TITLE}>{column.title}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/** `.comp-content_grey-box`: a row's own heading, on the tinted bar. */
function Strip({ row }: { row: Row }) {
  return (
    <Box sx={STRIP}>
      {row.icon ? (
        <LandingImage
          src={row.icon}
          sx={{ width: "18.08px", height: "18.08px", mr: "6.08px" }}
        />
      ) : null}
      <Typography sx={STRIP_HEADING}>{row.title}</Typography>
      {row.info ? (
        // The published wrap is a 24px line box around an inline image, which
        // is what sets the strip's height where a row carries this mark.
        <Box sx={{ display: "flex", alignItems: "center", minHeight: "24px" }}>
          <LandingImage
            src={row.info}
            sx={{ width: "14.08px", height: "14.08px" }}
          />
        </Box>
      ) : null}
      {row.note ? <Typography sx={STRIP_NOTE}>{row.note}</Typography> : null}
      {row.flags?.length ? (
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            justifyContent: "flex-end",
            width: 1,
          }}
        >
          {row.flags.map((flag) => (
            <LandingImage
              key={flag}
              src={flag}
              sx={{ width: "32px", height: "32px", objectFit: "cover" }}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * `.comp-content_txt strong`: one bullet, with the phrases the published line
 * emphasises. A plain string is the whole line, unemphasised.
 */
function Bullet({ value }: { value: ComparisonBullet }) {
  if (typeof value === "string") {
    return value;
  }
  return value.runs.map((run, index) =>
    run.bold ? (
      <Box key={index} component="strong" sx={{ fontWeight: 700 }}>
        {run.text}
      </Box>
    ) : (
      <Fragment key={index}>{run.text}</Fragment>
    )
  );
}

/**
 * One product's answer to one row, in the four shapes the page publishes.
 * `narrow` is the two-column table, which drops the rule under a ladder's
 * heading; `prose` is the opening section, which marks its values.
 */
function Cell({
  cell,
  prose,
  narrow,
}: {
  cell: ComparisonCell;
  prose: boolean;
  narrow?: boolean;
}) {
  if ("bullets" in cell) {
    // `comp-content_txt.is-list`: only the opening section marks its values.
    if (prose) {
      return (
        <Box component="ul" sx={[CELL_PROSE, { m: 0, pl: "16px" }]}>
          {cell.bullets.map((bullet, index) => (
            <Box
              component="li"
              key={index}
              sx={{ mb: "4.8px", fontSize: "inherit", lineHeight: "inherit" }}
            >
              <Bullet value={bullet} />
            </Box>
          ))}
        </Box>
      );
    }
    return (
      <Box sx={CELL_STACK}>
        {cell.bullets.map((bullet, index) => (
          <Typography key={index} sx={CELL_LINE}>
            <Bullet value={bullet} />
          </Typography>
        ))}
      </Box>
    );
  }
  if ("tiers" in cell) {
    return (
      <Box sx={CELL_COLUMN}>
        {cell.heading ? (
          <Typography sx={CELL_HEADING}>{cell.heading}</Typography>
        ) : null}
        {narrow ? null : <Box sx={RULE} />}
        {cell.tiers.map((tier) => (
          <Box
            key={tier.label}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              alignItems: "flex-start",
              width: 1,
            }}
          >
            <Typography sx={TIER_LABEL}>{tier.label}</Typography>
            <Box sx={{ display: "flex", gap: "4.8px", alignItems: "flex-end" }}>
              <Typography sx={TIER_PRICE}>{tier.price}</Typography>
              <Typography sx={TIER_UNIT}>{tier.per}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  if ("price" in cell) {
    return (
      <Box sx={CELL_COLUMN}>
        {cell.heading ? (
          <Typography sx={CELL_HEADING}>{cell.heading}</Typography>
        ) : null}
        <PriceFrom price={cell.price} per={cell.per} align="start" />
      </Box>
    );
  }
  return (
    <Box sx={CELL_STACK}>
      <Typography sx={CELL_LINE}>{cell.text}</Typography>
    </Box>
  );
}

/**
 * `.comp-content_box`: the three answers under a strip. The published table
 * runs them at 12 except under a strip that states a note, where they meet.
 */
function CellRow({ row, prose }: { row: Row; prose: boolean }) {
  return (
    <Box sx={{ display: "flex", px: "12px", gap: row.note ? 0 : "12px" }}>
      {row.cells.map((cell, index) => (
        <Cell key={index} cell={cell} prose={prose} />
      ))}
    </Box>
  );
}

/**
 * `.comp-content_title` and the rule under it. `comp-content_flex` sets the
 * one heading that carries a mark, which shrinks the heading to its own text.
 */
function BandTitle({ title, icon }: { title: string; icon?: string }) {
  const heading = (
    <Typography component="h3" sx={SECTION_TITLE}>
      <Lines text={title} />
    </Typography>
  );
  return (
    <>
      {icon ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6.08px" }}>
          <LandingImage src={icon} sx={{ width: "20px", height: "20px" }} />
          {heading}
        </Box>
      ) : (
        heading
      )}
      <Box sx={RULE} />
    </>
  );
}

/** `.comp-content_list`: the boxes, top to bottom. */
function DesktopTable({ bands }: { bands: ReadonlyArray<Band> }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: 1 }}>
      {bands.map((band, index) => (
        <Box
          key={bandKey(band)}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: 1,
            mb: index < LEAD_BOXES ? "80px" : "32px",
          }}
        >
          {band.title ? (
            <BandTitle title={band.title} icon={band.titleIcon} />
          ) : null}
          {band.parts.map(({ row, strip }) => (
            <Fragment key={row.title}>
              {strip ? <Strip row={row} /> : null}
              <CellRow row={row} prose={index === 0} />
            </Fragment>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/* ── the table below 992 ─────────────────────────────────────────────────── */

/** `.comp-content_title`, as the narrow table sets it. */
const MOBILE_TITLE = {
  color: marketingPalette.ink,
  fontSize: 18,
  fontWeight: 400,
  lineHeight: "21.6px",
  mb: "4.8px",
  WebkitTextStrokeWidth: "0.6px",
  [MID]: { fontSize: 16, lineHeight: "19.2px" },
} satisfies SxProps<Theme>;

/** `.comp-content_grey-box-heading`, which the narrow table sets in caps. */
const MOBILE_STRIP_HEADING = {
  color: marketingPalette.inkMuted,
  fontSize: 12.16,
  lineHeight: "14.592px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  WebkitTextStrokeWidth: "0.6px",
} as const;

/** `.dropdown_toggle`: the rule around a column's product picker. */
const PICKER_RULE = "#D5DADD";

/** `.comp-content_row`, as the narrow table sets one, and its three variants. */
const NARROW_ROW = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  p: "8px",
  mt: "8px",
  mb: "32px",
} as const;
/** `.is-summary`: the opening row, on no ground and at a stated height. */
const NARROW_LEAD = {
  minHeight: "144px",
  [TINY]: { height: "288px", px: 0 },
} satisfies SxProps<Theme>;
/** `.is-grey`: the tinted ground every other row takes. */
const NARROW_TINTED = { backgroundColor: marketingPalette.surface } as const;
/** `.is-case`: the same ground, at the height the published list states. */
const NARROW_CASE = {
  backgroundColor: marketingPalette.surface,
  [TINY]: { height: "128px" },
} satisfies SxProps<Theme>;

/**
 * Which of the three a row takes. The published page carries the class per row
 * and the three follow from what the row holds, so this reads them off the row
 * rather than asking the CMS for a name: the opening box is the lead, a listed
 * answer under a heading of its own is a case, and everything else is tinted.
 */
function narrowRowSx(part: BandPart, lead: boolean) {
  if (lead) {
    return NARROW_LEAD;
  }
  const listed = part.row.cells.some((cell) => "bullets" in cell);
  return !part.strip && listed ? NARROW_CASE : NARROW_TINTED;
}

/** `.comp-content_title`: a band's heading, stated once per shown column. */
function NarrowTitle({ title }: { title: string }) {
  return [0, 1].map((side) => (
    <Typography
      key={side}
      component={side === 0 ? "h3" : "p"}
      aria-hidden={side > 0}
      sx={[MOBILE_TITLE, side > 0 ? { visibility: "hidden" } : {}]}
    >
      <Lines text={title} />
    </Typography>
  ));
}

/**
 * Whether the narrow table states a row's own heading. It states every one but
 * the strip a flag row belongs to: the flags do not follow the table down, and
 * the published narrow markup drops the heading over them with them.
 */
function narrowStrips(part: BandPart): boolean {
  return part.strip && part.row.flags === undefined;
}

/** `.comp-content_grey-box`: a row's own heading, which the narrow table untints. */
function NarrowStrip({ row }: { row: Row }) {
  return [0, 1].map((side) => (
    <Box
      key={side}
      aria-hidden={side > 0}
      sx={[
        {
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "3.2px",
        },
        side > 0 ? { visibility: "hidden" } : {},
      ]}
    >
      <Typography sx={MOBILE_STRIP_HEADING}>{row.title}</Typography>
      {row.note ? (
        <Typography sx={[STRIP_NOTE, { textAlign: "left" }]}>
          {row.note}
        </Typography>
      ) : null}
    </Box>
  ));
}

/** One row's answers: the cell each shown column holds for it. */
function NarrowCells({
  part,
  picked,
  lead,
}: {
  part: BandPart;
  picked: readonly [number, number];
  lead: boolean;
}) {
  return picked.map((product, side) => {
    const cell = part.row.cells[product];
    return (
      <Box key={side} sx={[NARROW_ROW, narrowRowSx(part, lead)]}>
        {cell ? <Cell cell={cell} prose={lead} narrow /> : null}
      </Box>
    );
  });
}

/**
 * `.compare-filter`: which product a column is showing. The published page
 * draws a Webflow dropdown; a select is the same control with the platform's
 * own keyboard and screen-reader behaviour.
 */
function ProductPicker({
  block,
  value,
  taken,
  onChange,
}: {
  block: Comparison;
  value: number;
  taken: number;
  onChange: (next: number) => void;
}) {
  return (
    <Box sx={{ position: "relative", display: "flex" }}>
      <Box
        component="select"
        aria-label="Product to compare"
        value={value}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
          onChange(Number(event.target.value))
        }
        sx={{
          width: 1,
          height: "48px",
          pl: "16px",
          pr: "40px",
          borderRadius: "12px",
          border: `1px solid ${PICKER_RULE}`,
          backgroundColor: "common.white",
          color: marketingPalette.inkMuted,
          font: "inherit",
          fontSize: 15.04,
          fontWeight: 500,
          lineHeight: "17.6px",
          appearance: "none",
          [TINY]: { pl: "12px" },
        }}
      >
        {block.columns.map((column, index) => (
          <option key={column.title} value={index} disabled={index === taken}>
            {column.title}
          </option>
        ))}
      </Box>
      {/* `.dropdown_arrow`: the caret the published toggle draws against its
          right padding. The select owns the interaction, so this is inert. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: "16px",
          top: "50%",
          display: "flex",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          color: marketingPalette.inkMuted,
          [TINY]: { right: "12px" },
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path
            d="M5 7.25 9 11.25 13 7.25"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    </Box>
  );
}

/**
 * `.compare-mob_wrap`: two products side by side. The published columns are two
 * independent stacks held level by a fixed height on three of their rows; one
 * grid does the same for every row without a number, so a longer answer on
 * either side lines up rather than needing one.
 */
function MobileTable({
  block,
  bands,
}: {
  block: Comparison;
  bands: ReadonlyArray<Band>;
}) {
  const [picked, setPicked] = useState<[number, number]>([0, 1]);
  const pick = (side: 0 | 1) => (next: number) =>
    setPicked((current) =>
      side === 0 ? [next, current[1]] : [current[0], next]
    );
  /** The published column states its offer twice: over the table and under it. */
  const offerCards = (place: string) =>
    picked.map((product, side) => {
      const offer = block.offers[product];
      return offer ? (
        <Box
          key={`${place}-${side}`}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            p: "16px",
            mb: "16px",
            borderRadius: "8px",
            backgroundColor: marketingPalette.surface,
          }}
        >
          <PriceFrom price={offer.from} per={offer.per} align="centre" />
          <OfferActions offer={offer} wide />
        </Box>
      ) : (
        <Box key={`${place}-${side}`} />
      );
    });
  return (
    // `.compare-mob_wrap`: the two columns clear the hero by 32, and by 16 once
    // the picker takes a margin of its own.
    <Box sx={{ width: 1, mt: "32px", [MID]: { mt: "16px" } }}>
      <Box
        sx={{
          position: "sticky",
          top: "55px",
          zIndex: 20,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          backgroundColor: "common.white",
          p: "8px",
          [MID]: { mt: "16px", p: 0 },
          [TINY]: { top: "45px", gap: "8px", mt: 0, pt: "16px", pb: "8px" },
        }}
      >
        {[0, 1].map((side) => (
          <ProductPicker
            key={side}
            block={block}
            value={picked[side]}
            taken={picked[side === 0 ? 1 : 0]}
            onChange={pick(side === 0 ? 0 : 1)}
          />
        ))}
      </Box>
      {/* `.comp-content-box.is-mobile`: the run the picker sits at the head of,
          which opens wider once the picker loses its own padding. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: "16px",
          mt: "16px",
          [MID]: { mt: "24px" },
          [TINY]: { columnGap: "8px" },
        }}
      >
        {offerCards("top")}
        {bands.map((band, index) => (
          <Fragment key={bandKey(band)}>
            {band.title ? <NarrowTitle title={band.title} /> : null}
            {band.parts.map((part) => (
              <Fragment key={part.row.title}>
                {narrowStrips(part) ? <NarrowStrip row={part.row} /> : null}
                <NarrowCells part={part} picked={picked} lead={index === 0} />
              </Fragment>
            ))}
          </Fragment>
        ))}
        {offerCards("bottom")}
      </Box>
    </Box>
  );
}

/* ── the two blocks the page states ──────────────────────────────────────── */

/**
 * `header-compare_wrap.is-last`: the table closes on a repeat of its offers,
 * which states the offers and no attributes, so an empty row list is what marks
 * one. The published page drops that repeat below 992 along with the wide table
 * it belongs to.
 */
function ClosingPanel({ block }: { block: Comparison }) {
  return (
    <LandingSection
      padding="none"
      sx={{ pt: "32px", pb: "100px", [NOT_WIDE]: { display: "none" } }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "40px",
          p: "32px",
          backgroundColor: marketingPalette.surface,
        }}
      >
        <Typography component="h2" sx={HEADING_2}>
          <Lines text={block.title} />
        </Typography>
        <ProductCards block={block} />
      </Box>
    </LandingSection>
  );
}

export default function ComparisonBlock({ block }: { block: Comparison }) {
  const table = useRef<HTMLDivElement>(null);
  const bands = placeRows(block.rows);
  if (block.rows.length === 0) {
    return <ClosingPanel block={block} />;
  }
  return (
    <LandingSection
      padding="none"
      sx={{ pb: 0, [NOT_WIDE]: { pb: "64px" }, [TINY]: { pb: "16px" } }}
    >
      {/* `.header-compare_wrap`: the 40 the published wrap sets between its own
          heading and the product row. The heading is the hero block above, so
          the gap is this block's to keep; the narrow layout states its own. */}
      <Box sx={{ pt: "40px", [NOT_WIDE]: { display: "none" } }}>
        <ProductCards block={block} />
        <StickyProductBar block={block} table={table} />
        <Box ref={table} sx={{ mt: "40px" }}>
          <DesktopTable bands={bands} />
        </Box>
      </Box>
      <Box sx={{ display: "none", [NOT_WIDE]: { display: "block" } }}>
        <MobileTable block={block} bands={bands} />
      </Box>
    </LandingSection>
  );
}
